import { TextField, Typography } from '@mui/material';
import React from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { borderRadius, palette, spaces } from '../../../assets/styles/themes/tokens';
import { useId } from '../../../utils/useId';

interface OwnProps {
	name: string;
	control: any;
	defaultValue: string;
	isEditMode: boolean;
	error?: string;
	label?: string;
	'aria-label'?: string;
}

const EditableTextField: React.FC<OwnProps> = ({
	name,
	control,
	defaultValue,
	isEditMode,
	error,
	label,
	'aria-label': ariaLabel,
}) => {
	const { t } = useTranslation();
	const fieldId = useId(name);
	const errorId = `${fieldId}-error`;
	const helperTextId = error ? errorId : undefined;

	return isEditMode ? (
		<Controller
			name={name}
			control={control}
			defaultValue={defaultValue}
			as={
				<TextField
					id={fieldId}
					fullWidth
					size="small"
					variant="outlined"
					error={!!error}
					helperText={error ? t(error) : ''}
					aria-label={ariaLabel || label || name}
					aria-describedby={helperTextId}
					aria-invalid={!!error || undefined}
					FormHelperTextProps={{
						id: helperTextId,
						role: error ? 'alert' : undefined,
					}}
					InputProps={{
						style: {
							borderRadius: borderRadius.sm,
						},
						sx: {
							'& .MuiInputBase-input': {
								padding: `${spaces.xs} ${spaces.sm}`,
							},
							'& fieldset': {
								borderColor: palette.components.input.outlined.enabledBorder,
							},
						},
					}}
				/>
			}
		/>
	) : (
		<Typography
			color={defaultValue ? 'text.primary' : 'text.disabled'}
			sx={{
				fontWeight: 400,
				letterSpacing: '0.15px',
			}}
			aria-label={label || name}
		>
			{defaultValue || t('Not set')}
		</Typography>
	);
};

export default EditableTextField;
